// src/components/AuthModal.jsx
// Signup / Login / Forgot Password modal

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth as authApi } from '../api/client';

const VIEWS = { LOGIN: 'login', SIGNUP: 'signup', FORGOT: 'forgot', VERIFY: 'verify' };

const Input = ({ label, type = 'text', value, onChange, placeholder, error, autoFocus }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{ display: 'block', fontSize: '10px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '6px' }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      style={{
        width: '100%',
        padding: '11px 14px',
        background: 'var(--deep)',
        border: `1px solid ${error ? 'var(--red)' : 'var(--b1)'}`,
        color: 'var(--t1)',
        fontSize: '13px',
        fontFamily: 'JetBrains Mono',
        borderRadius: '3px',
        outline: 'none',
        letterSpacing: '.04em',
        boxSizing: 'border-box',
        transition: 'border .15s',
      }}
      onFocus={(e) => { e.target.style.borderColor = 'var(--cyan)'; }}
      onBlur={(e) => { e.target.style.borderColor = error ? 'var(--red)' : 'var(--b1)'; }}
    />
    {error && <div style={{ fontSize: '10px', color: 'var(--red)', marginTop: '4px' }}>! {error}</div>}
  </div>
);

const PasswordStrength = ({ pw }) => {
  const checks = [pw.length >= 8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)];
  const score = checks.filter(Boolean).length;
  const colors = ['#f87171', '#fb923c', '#fbbf24', '#4ade80'];
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  if (!pw) return null;

  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '5px' }}>
        {checks.map((ok, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '3px',
              borderRadius: '2px',
              background: i < score ? colors[score - 1] : 'var(--b0)',
              transition: 'background .3s',
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: '9px', color: colors[score - 1] || 'var(--t3)', letterSpacing: '.08em' }}>
        {score > 0 ? labels[score - 1] : ''} {score === 4 ? 'OK' : ''}
      </div>
    </div>
  );
};

export default function AuthModal({ onClose, defaultView = VIEWS.LOGIN }) {
  const {
    login,
    register,
    verifyEmailCode,
    resendVerificationCode,
    error: authError,
    setError,
  } = useAuth();

  const [view, setView] = useState(defaultView);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState({});
  const [forgotSent, setForgotSent] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyMsg, setVerifyMsg] = useState('');
  const overlayRef = useRef();

  useEffect(() => {
    setLocalErr({});
    setVerifyMsg('');
    setError?.(null);
  }, [view, setError]);

  const validate = () => {
    const errs = {};

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errs.email = 'Valid email required';

    if (view === VIEWS.SIGNUP) {
      if (!fullName || fullName.trim().length < 3) errs.full_name = 'Min 3 characters';
      if (fullName && fullName.trim().length > 40) errs.full_name = 'Max 40 characters';
      if (fullName && !/^[A-Za-z][A-Za-z\s'.-]*$/.test(fullName.trim())) {
        errs.full_name = 'Use letters, spaces, apostrophes, dots, hyphens';
      }
      if (password.length < 8) errs.password = 'Min 8 characters';
      if (password !== confirm) errs.confirm = 'Passwords do not match';
    }

    if (view === VIEWS.VERIFY && !/^\d{6}$/.test(code.trim())) errs.code = 'Enter the 6-digit code';
    if (view === VIEWS.LOGIN && !password) errs.password = 'Required';

    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setLocalErr(errs);
      return;
    }

    setLocalErr({});
    setBusy(true);
    try {
      if (view === VIEWS.LOGIN) {
        const ok = await login({ email, password });
        if (ok) onClose?.();
        return;
      }

      if (view === VIEWS.SIGNUP) {
        const data = await register({ email, full_name: fullName.trim(), password });
        if (data?.user) {
          onClose?.();
          return;
        }
        if (data) {
          setVerifyEmail(data.email || email);
          setCode('');
          setVerifyMsg(data.message || 'We sent a 6-digit verification code to your email.');
          setView(VIEWS.VERIFY);
        }
        return;
      }

      if (view === VIEWS.VERIFY) {
        const data = await verifyEmailCode({ email: verifyEmail || email, code: code.trim() });
        if (data?.user) onClose?.();
        return;
      }

      if (view === VIEWS.FORGOT) {
        await authApi.forgotPassword(email);
        setForgotSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResendCode = async () => {
    setLocalErr({});
    setVerifyMsg('');
    setBusy(true);
    try {
      const data = await resendVerificationCode(verifyEmail || email);
      if (data) setVerifyMsg(data.message || 'A new verification code was sent.');
    } finally {
      setBusy(false);
    }
  };

  const globalErr = authError;

  return (
    <div
      ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,5,8,.88)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          background: 'var(--surface)',
          border: '1px solid var(--b1)',
          borderRadius: '6px',
          animation: 'authSlideUp .25s cubic-bezier(.16,1,.3,1)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid var(--b0)' }}>
          {[VIEWS.LOGIN, VIEWS.SIGNUP].map((tab) => (
            <button
              key={tab}
              onClick={() => setView(tab)}
              style={{
                flex: 1,
                padding: '16px',
                background: 'none',
                border: 'none',
                borderBottom: view === tab ? '2px solid var(--cyan)' : '2px solid transparent',
                color: view === tab ? 'var(--cyan)' : 'var(--t3)',
                fontSize: '10px',
                letterSpacing: '.14em',
                cursor: 'pointer',
                fontFamily: 'JetBrains Mono',
                fontWeight: view === tab ? 700 : 400,
                textTransform: 'uppercase',
                transition: 'all .15s',
              }}
            >
              {tab === VIEWS.LOGIN ? 'SIGN IN' : 'CREATE ACCOUNT'}
            </button>
          ))}
          <button
            onClick={onClose}
            style={{
              width: '52px',
              background: 'none',
              border: 'none',
              color: 'var(--t3)',
              cursor: 'pointer',
              fontSize: '16px',
              borderLeft: '1px solid var(--b0)',
            }}
          >
            X
          </button>
        </div>

        <div style={{ padding: '28px 28px 24px' }}>
          {view === VIEWS.VERIFY && (
            <form onSubmit={handleSubmit}>
              {globalErr && <div style={errBox}>{globalErr}</div>}
              {verifyMsg && <div style={okBox}>{verifyMsg}</div>}
              <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>MAIL</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '18px', marginBottom: '10px' }}>
                  Enter verification code
                </div>
                <p style={{ fontSize: '12px', color: 'var(--t2)', lineHeight: 1.8, marginBottom: '20px' }}>
                  We sent a 6-digit code to
                  <br />
                  <span style={{ color: 'var(--cyan)', fontWeight: 700 }}>{verifyEmail || email}</span>
                </p>
              </div>
              <Input
                label="6-DIGIT CODE"
                value={code}
                onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                error={localErr.code}
                autoFocus
              />
              <button type="submit" disabled={busy} style={primaryBtn(busy)}>
                {busy ? 'VERIFYING...' : 'VERIFY EMAIL ->'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '14px', fontSize: '11px' }}>
                <span onClick={handleResendCode} style={{ color: 'var(--cyan)', cursor: 'pointer' }}>
                  Resend code
                </span>
                <span onClick={() => setView(VIEWS.SIGNUP)} style={{ color: 'var(--t3)', cursor: 'pointer' }}>
                  Back to sign up
                </span>
              </div>
            </form>
          )}

          {view === VIEWS.FORGOT && !forgotSent && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '18px', marginBottom: '6px' }}>
                  Reset password
                </div>
                <p style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.6 }}>
                  Enter your email and we&apos;ll send a reset link.
                </p>
              </div>
              <Input
                label="EMAIL"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                error={localErr.email}
                autoFocus
              />
              <button onClick={handleSubmit} disabled={busy} style={primaryBtn(busy)}>
                {busy ? 'SENDING...' : 'SEND RESET LINK ->'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '14px' }}>
                <span onClick={() => setView(VIEWS.LOGIN)} style={{ fontSize: '11px', color: 'var(--cyan)', cursor: 'pointer' }}>
                  {'<- Back to sign in'}
                </span>
              </div>
            </>
          )}

          {view === VIEWS.FORGOT && forgotSent && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>MAIL</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '16px', marginBottom: '8px' }}>
                Reset link sent
              </div>
              <p style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '16px' }}>
                Check {email} - link expires in 1 hour.
              </p>
              <span
                onClick={() => {
                  setView(VIEWS.LOGIN);
                  setForgotSent(false);
                }}
                style={{ fontSize: '11px', color: 'var(--cyan)', cursor: 'pointer' }}
              >
                {'<- Back to sign in'}
              </span>
            </div>
          )}

          {view === VIEWS.LOGIN && (
            <form onSubmit={handleSubmit}>
              {globalErr && <div style={errBox}>{globalErr}</div>}
              <Input
                label="EMAIL"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                error={localErr.email}
                autoFocus
              />
              <Input
                label="PASSWORD"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="........"
                error={localErr.password}
              />
              <div style={{ textAlign: 'right', marginTop: '-8px', marginBottom: '16px' }}>
                <span onClick={() => setView(VIEWS.FORGOT)} style={{ fontSize: '10px', color: 'var(--cyan)', cursor: 'pointer' }}>
                  Forgot password?
                </span>
              </div>
              <button type="submit" disabled={busy} style={primaryBtn(busy)}>
                {busy ? 'SIGNING IN...' : 'SIGN IN ->'}
              </button>
              
            </form>
          )}

          {view === VIEWS.SIGNUP && (
            <form onSubmit={handleSubmit}>
              {globalErr && <div style={errBox}>{globalErr}</div>}
              <Input
                label="FULL NAME"
                value={fullName}
                onChange={setFullName}
                placeholder="Satoshi Nakamoto"
                error={localErr.full_name}
                autoFocus
              />
              <Input
                label="EMAIL"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                error={localErr.email}
              />
              <Input
                label="PASSWORD"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="min 8 characters"
                error={localErr.password}
              />
              <PasswordStrength pw={password} />
              <Input
                label="CONFIRM PASSWORD"
                type="password"
                value={confirm}
                onChange={setConfirm}
                placeholder="repeat password"
                error={localErr.confirm}
              />
              <p style={{ fontSize: '10px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '16px' }}>
                By signing up you agree to our <a href="/terms" style={{ color: 'var(--cyan)' }} target="_blank" rel="noreferrer">Terms</a> and{' '}
                <a href="/privacy" style={{ color: 'var(--cyan)' }} target="_blank" rel="noreferrer">Privacy Policy</a>.
                
              </p>
              <button type="submit" disabled={busy} style={primaryBtn(busy)}>
                {busy ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT ->'}
              </button>
            </form>
          )}
        </div>

        <div
          style={{
            padding: '12px 28px',
            background: 'var(--deep)',
            borderTop: '1px solid var(--b0)',
            display: 'flex',
            gap: '20px',
          }}
        >
          {[ 'No credit card', 'Free Forever with limits'].map((item) => (
            <span key={item} style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.08em' }}>
              + {item}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes authSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(.98); }
          to { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

const primaryBtn = (busy) => ({
  width: '100%',
  padding: '13px',
  background: busy ? 'var(--b0)' : 'var(--cyan)',
  border: 'none',
  borderRadius: '3px',
  color: busy ? 'var(--t3)' : '#020508',
  fontSize: '11px',
  fontFamily: 'JetBrains Mono',
  fontWeight: 700,
  letterSpacing: '.12em',
  cursor: busy ? 'not-allowed' : 'pointer',
  transition: 'all .15s',
});

const errBox = {
  padding: '10px 12px',
  background: 'var(--red)18',
  border: '1px solid var(--red)44',
  borderRadius: '3px',
  fontSize: '11px',
  color: 'var(--red)',
  marginBottom: '14px',
};

const okBox = {
  padding: '10px 12px',
  background: 'var(--green)18',
  border: '1px solid var(--green)44',
  borderRadius: '3px',
  fontSize: '11px',
  color: 'var(--green)',
  marginBottom: '14px',
};
